/**
 * MP pack — fixed palette for per-parcel map / SVG fills. Now sourced from the
 * shared AURORA per-parcel palette (single source of truth in
 * `config/siteLayerColors.ts`) so every per-parcel surface — this multi-parcel
 * drawer, the at-a-glance card, the boundary thumbnail and the live Google-Maps
 * boundary background — draws the SAME Aurora hues in the SAME order, keeping the
 * map outlines and legend dots one-to-one.
 */
export { AURORA_PARCEL_COLORS as PARCEL_COLORS } from '../config/siteLayerColors';
import { AURORA_PARCEL_COLORS } from '../config/siteLayerColors';
import type { SiteBoundaryParcel } from '../types/siteBoundary';

export function parcelColorIndex(i: number): number {
  return i % AURORA_PARCEL_COLORS.length;
}

/** A parcel's display entry for the map layer panel / legend: the stable
 *  parcel id + a human-readable, DISAMBIGUATED name. */
export interface ParcelDisplayEntry {
  id: string;
  name: string;
}

/**
 * Build display names for a parcel list, disambiguating duplicates.
 *
 * KMZ imports routinely repeat an owner name across several disjoint
 * parcels (e.g. "HEMBERGER LAND, INC" appears on 3 separate parcels).
 * Rendering three identical rows in the layer panel is useless, so any
 * label shared by >1 parcel is suffixed with a 1-based occurrence index
 * ("HEMBERGER LAND, INC (1)", "(2)", "(3)"). Labels that are unique stay
 * untouched. Unlabeled parcels fall back to "Parcel N" (N = 1-based
 * position), matching {@link SiteBoundaryParcelLegend}.
 *
 * Pure — no color assignment (each surface owns its own palette rule) and
 * no Google-Maps dependency, so it is unit-testable in isolation.
 */
export function buildParcelDisplayEntries(
  parcels: ReadonlyArray<Pick<SiteBoundaryParcel, 'id' | 'label'>>,
): ParcelDisplayEntry[] {
  const baseNameFor = (p: Pick<SiteBoundaryParcel, 'id' | 'label'>, i: number): string =>
    (p.label?.trim() || `Parcel ${i + 1}`);

  const counts = new Map<string, number>();
  parcels.forEach((p, i) => {
    const base = baseNameFor(p, i);
    counts.set(base, (counts.get(base) ?? 0) + 1);
  });

  const seen = new Map<string, number>();
  return parcels.map((p, i) => {
    const base = baseNameFor(p, i);
    if ((counts.get(base) ?? 0) > 1) {
      const k = (seen.get(base) ?? 0) + 1;
      seen.set(base, k);
      return { id: p.id, name: `${base} (${k})` };
    }
    return { id: p.id, name: base };
  });
}
